import { ButtonV2 } from '@cimplico/components';
import { ArrowUpCircleIcon } from '@heroicons/react/24/outline';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import { useHasFeatureAccess } from '../../hooks/use-has-feature-access';
import { useIsFirmRole } from '../../hooks/use-is-firm-role';
import { useFirmContext } from '../../providers/FirmProvider';
import { useReadNotifications } from '../../queries/use-read-notifications';
import { useUserNotifications } from '../../queries/use-user-notifications';
import NotificationRow from './NotificationRow';

function Preview(): JSX.Element {
    const navigate = useNavigate();
    const { shortId } = useFirmContext();
    const { isAdmin } = useIsFirmRole();

    return (
        <div className="flex flex-col justify-center items-center py-4 px-6 rounded-md w-[300px]">
            <LockClosedIcon className="h-5 w-5 text-gray-600 dark:text-gray-200" />
            <p className="text-gray-600 dark:text-gray-200 mb-2 text-center text-xs">
                Upgrade to the <b>Essential</b> or <b>Professional</b> plan to access this feature
            </p>
            <ButtonV2
                size="xs"
                label="Upgrade"
                leftIcon={<ArrowUpCircleIcon />}
                onClick={(): void => {
                    navigate(`/${shortId}/settings`);
                }}
                isDisabled={!isAdmin}
            />
            {!isAdmin && (
                <p className="text-gray-600 dark:text-gray-200 mt-2 text-xs text-center">
                    To upgrade, please contact your firm&apos;s administrator
                </p>
            )}
        </div>
    );
}

function NotificationMenuBody(): JSX.Element {
    const canAccessNotifications = useHasFeatureAccess('notifications:read');

    const userNotificationsQuery = useUserNotifications();
    const readNotificationsQuery = useReadNotifications();

    if (userNotificationsQuery.isLoading) {
        return <></>;
    }

    if (userNotificationsQuery.isError || !userNotificationsQuery.isSuccess) {
        return <p>Error!</p>;
    }

    const { userNotifications } = userNotificationsQuery.data;

    function handleReadAll(): void {
        const unreadNotifications = userNotifications.filter((n) => !n.isRead);

        let allNotificationIds: string[] = [];
        for (const unreadNotification of unreadNotifications) {
            if (unreadNotification.relatedNotifications) {
                allNotificationIds = allNotificationIds.concat(unreadNotification.relatedNotifications);
            } else {
                allNotificationIds = allNotificationIds.concat([unreadNotification.notification.id]);
            }
        }

        readNotificationsQuery.mutate({
            ids: allNotificationIds,
        });
    }

    return (
        <>
            {!canAccessNotifications && <Preview />}

            {canAccessNotifications! && userNotifications.length === 0 && (
                <p className="block text-center text-sm text-gray-500 w-[230px] p-4">
                    You have no new notifications from the last 30 days.
                </p>
            )}

            {canAccessNotifications && userNotifications.length > 0 && (
                <>
                    <div className="flex justify-between py-3 border-b px-4">
                        <p className="text-sm text-gray-800 font-bold dark:text-white/[0.87]">Notifications</p>
                        <button className="text-start text-gray-600 text-xs font-medium" onClick={handleReadAll}>
                            Mark all as read
                        </button>
                    </div>
                    <div className="max-h-[500px] overflow-y-auto">
                        {userNotifications
                            .sort((a, b) => (a.notification.createdAt > b.notification.createdAt ? -1 : 1))
                            .map((notification, idx) => {
                                return (
                                    <NotificationRow
                                        key={notification.notification.id}
                                        userNotification={notification}
                                        isFirst={idx === 0}
                                    />
                                );
                            })}
                    </div>
                </>
            )}
        </>
    );
}

export default NotificationMenuBody;
